// Linear algebra utilities: LU decomposition with partial pivoting and solvers
function cloneMatrix(A) {
    return A.map((row) => row.slice());
}

function luDecompose(A) {
    const n = A.length;
    if (!A.every((row) => row.length === n)) throw new Error('Matrix must be square');

    const LU = cloneMatrix(A);
    const p = new Array(n);
    for (let i = 0; i < n; i++) p[i] = i;
    let singular = false;
    const tiny = 1e-12;

    for (let k = 0; k < n; k++) {
        // partial pivoting: find pivot row
        let max = 0;
        let piv = k;
        for (let i = k; i < n; i++) {
            const v = Math.abs(LU[i][k]);
            if (v > max) {
                max = v;
                piv = i;
            }
        }

        if (max < tiny) {
            singular = true;
            break;
        }

        if (piv !== k) {
            const tmp = LU[k];
            LU[k] = LU[piv];
            LU[piv] = tmp;
            const tp = p[k];
            p[k] = p[piv];
            p[piv] = tp;
        }

        // elimination
        for (let i = k + 1; i < n; i++) {
            LU[i][k] = LU[i][k] / LU[k][k];
            const mult = LU[i][k];
            for (let j = k + 1; j < n; j++) LU[i][j] -= mult * LU[k][j];
        }
    }

    return { LU, p, singular };
}

function luSolve(decomp, b) {
    const { LU, p, singular } = decomp;
    const n = LU.length;
    if (singular) throw new Error('Matrix is singular or near-singular');

    const isMulti = Array.isArray(b[0]);

    if (!isMulti) {
        // single RHS vector
        const pb = new Array(n);
        for (let i = 0; i < n; i++) pb[i] = b[p[i]];

        // forward substitution Ly = pb (L has unit diagonal stored in LU lower part)
        const y = new Array(n);
        for (let i = 0; i < n; i++) {
            let sum = pb[i];
            for (let j = 0; j < i; j++) sum -= LU[i][j] * y[j];
            y[i] = sum;
        }

        // backward substitution Ux = y
        const x = new Array(n);
        for (let i = n - 1; i >= 0; i--) {
            let sum = y[i];
            for (let j = i + 1; j < n; j++) sum -= LU[i][j] * x[j];
            x[i] = sum / LU[i][i];
        }

        return x;
    }

    // multiple RHS: b is an array of vectors [[...],[...],...]
    const m = b.length;
    const xs = new Array(m);
    for (let k = 0; k < m; k++) xs[k] = luSolve(decomp, b[k]);
    return xs;
}

function solve(A, b) {
    const decomp = luDecompose(A);
    return luSolve(decomp, b);
}

export default { luDecompose, luSolve, solve };
